import { z } from "zod"

// Package and folder edits applyPackageOps runs in order: savePackage translates
// the wizard's row into them, Settings saves staged store lists as
// setPackageStores ops, and the package and folder pages send the rest.

export const MAX_PACKAGE_OPS = 500
const MAX_IDS = 50_000

const utcDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a YYYY-MM-DD date")
const id = z.string().min(1).max(128)
const ids = z.array(id).max(MAX_IDS)

/** UTC calendar dates, both inclusive; null is always open. */
const windowSchema = z
  .object({ startDate: utcDate, cutoffDate: utcDate })
  .refine((window) => window.startDate <= window.cutoffDate, {
    message: "The replenishment cut-off cannot be before the start date.",
  })
  .nullable()

export const REPLENISHMENT_TYPES = ["seasonal", "noos", "clearance"] as const
export type ReplenishmentType = (typeof REPLENISHMENT_TYPES)[number]
export const PACKAGE_NAME_MAX = 120
const entityName = (noun: string) =>
  z.string().trim().min(3, `A ${noun} name needs at least 3 characters.`).max(PACKAGE_NAME_MAX)
export const packageName = entityName("package")
export const folderName = entityName("folder")

export const packageStoresOpSchema = z.object({
  kind: z.literal("setPackageStores"),
  packageId: id,
  add: ids,
  remove: ids,
})

const renamePackageOpSchema = z.object({
  kind: z.literal("renamePackage"),
  packageId: id,
  name: packageName,
})

/** Moves the window; each shift flag moves that bound of every variant's own dates by the same days. */
export const setPackageWindowOpSchema = z.object({
  kind: z.literal("setPackageWindow"),
  packageId: id,
  window: windowSchema,
  shiftOwnStart: z.boolean(),
  shiftOwnCutoff: z.boolean(),
})

/** A new package (packageId) with the source's type, window, SKUs with their own dates, and stores. */
export const duplicatePackageOpSchema = z.object({
  kind: z.literal("duplicatePackage"),
  packageId: id,
  sourceId: id,
  name: packageName,
})

/**
 * Into a folder, or out of every folder with null. The package keeps the
 * dates it had, writing a followed window down as its own. stores "keep" keeps
 * the stores it had as its own list; "follow" follows the target folder's.
 */
const movePackageOpSchema = z.object({
  kind: z.literal("movePackage"),
  packageId: id,
  folderId: id.nullable(),
  stores: z.enum(["keep", "follow"]),
})

/** ownStores false follows the folder's stores; true starts the package's own list from them. */
const setPackageStoresSourceOpSchema = z.object({
  kind: z.literal("setPackageStoresSource"),
  packageId: id,
  ownStores: z.boolean(),
})

/** Archive (false) or bring back (true). */
const setPackageActiveOpSchema = z.object({
  kind: z.literal("setPackageActive"),
  packageId: id,
  active: z.boolean(),
})

/** Soft delete: the rows stay for the history, readers skip the package. */
export const deletePackageOpSchema = z.object({ kind: z.literal("deletePackage"), packageId: id })

export const packagePageOpSchema = z.discriminatedUnion("kind", [
  renamePackageOpSchema,
  setPackageWindowOpSchema,
  duplicatePackageOpSchema,
  movePackageOpSchema,
  setPackageStoresSourceOpSchema,
  setPackageActiveOpSchema,
])

export const folderOpSchema = z.discriminatedUnion("kind", [
  /** parentId makes it a subfolder; subfolders cannot hold subfolders. */
  z.object({
    kind: z.literal("createFolder"),
    folderId: id,
    name: folderName,
    parentId: id.nullable(),
    window: windowSchema,
  }),
  z.object({ kind: z.literal("renameFolder"), folderId: id, name: folderName }),
  z.object({ kind: z.literal("moveFolder"), folderId: id, parentId: id.nullable() }),
  /** null on a subfolder follows its parent; on a top-level folder it leaves the folder undated. */
  z.object({ kind: z.literal("setFolderWindow"), folderId: id, window: windowSchema }),
  /** The folder's stores, and every package and subfolder inside it follows them. */
  z.object({ kind: z.literal("setFolderStores"), folderId: id, storeIds: ids }),
  /** A subfolder's own stores (true, starting from its parent's) or its parent's (false). */
  z.object({ kind: z.literal("setFolderStoresSource"), folderId: id, ownStores: z.boolean() }),
  z.object({ kind: z.literal("setFolderActive"), folderId: id, active: z.boolean() }),
  /** The folder, its subfolders and every package in them. */
  z.object({ kind: z.literal("deleteFolder"), folderId: id }),
  /**
   * Removes a subfolder; its packages move up into the parent. What they
   * followed from the subfolder (its own dates, its own stores) becomes their
   * own, so nothing they replenish changes.
   */
  z.object({ kind: z.literal("removeSubfolder"), folderId: id }),
  /** Hand-set order among siblings: each list's position becomes its sortOrder. */
  z.object({
    kind: z.literal("reorder"),
    packageIds: ids.max(MAX_PACKAGE_OPS),
    folderIds: ids.max(MAX_PACKAGE_OPS),
  }),
  /** A copy (folderId) of the folder, its subfolders, stores and packages. */
  z.object({
    kind: z.literal("duplicateFolder"),
    folderId: id,
    sourceId: id,
    name: folderName,
  }),
])

export const packageOpSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("createPackage"),
    packageId: id,
    name: packageName,
    replenishmentType: z.enum(REPLENISHMENT_TYPES),
    window: windowSchema,
    folderId: id.nullable().optional(),
  }),
  renamePackageOpSchema,
  packageStoresOpSchema,
  z.object({
    kind: z.literal("addVariants"),
    packageId: id,
    variants: z
      .array(z.object({ variantId: id, skuIds: ids.optional() }))
      .min(1)
      .max(MAX_IDS),
  }),
  z.object({ kind: z.literal("removeVariants"), packageId: id, variantIds: ids.min(1) }),
  z.object({ kind: z.literal("setVariantSizes"), packageId: id, variantId: id, skuIds: ids }),
  setPackageWindowOpSchema,
  duplicatePackageOpSchema,
  movePackageOpSchema,
  setPackageStoresSourceOpSchema,
  setPackageActiveOpSchema,
  deletePackageOpSchema,
  ...folderOpSchema.options,
])

export type PackageOp = z.infer<typeof packageOpSchema>
export type FolderOp = z.infer<typeof folderOpSchema>
export type PackageOpOf<K extends PackageOp["kind"]> = Extract<PackageOp, { kind: K }>
export type PackageStoresOp = z.infer<typeof packageStoresOpSchema>
export type PackagePageOp = z.infer<typeof packagePageOpSchema>

/** Whether an op can put a SKU in a store it was not in before. */
const ADDS_PAIRS: {
  [K in PackageOp["kind"]]: boolean | ((op: Extract<PackageOp, { kind: K }>) => boolean)
} = {
  createPackage: false,
  renamePackage: false,
  setPackageStores: (op) => op.add.length > 0,
  addVariants: true,
  removeVariants: false,
  setVariantSizes: true,
  setPackageWindow: false,
  duplicatePackage: true,
  movePackage: true,
  setPackageStoresSource: true,
  setPackageActive: (op) => op.active,
  deletePackage: false,
  createFolder: false,
  renameFolder: false,
  moveFolder: true,
  setFolderWindow: false,
  setFolderStores: true,
  setFolderStoresSource: true,
  setFolderActive: (op) => op.active,
  deleteFolder: false,
  removeSubfolder: false,
  reorder: false,
  duplicateFolder: true,
}

export function addsPackagePairs(ops: PackageOp[]): boolean {
  return ops.some((op) => {
    const rule = ADDS_PAIRS[op.kind] as boolean | ((op: PackageOp) => boolean)
    return typeof rule === "function" ? rule(op) : rule
  })
}

const FOLDER_OP_KINDS: ReadonlySet<string> = new Set(
  folderOpSchema.options.map((option) => option.shape.kind.value),
)

export const isFolderOp = (op: PackageOp): op is FolderOp => FOLDER_OP_KINDS.has(op.kind)

/** The packages an op names: the one it edits, plus a duplicate's source. Folder ops name none. */
export function lockedPackageIds(op: PackageOp): string[] {
  if (isFolderOp(op)) return []
  return op.kind === "duplicatePackage" ? [op.packageId, op.sourceId] : [op.packageId]
}

/** The folders whose packages a folder op changes, so they are locked and diffed too. */
export function opFolderIds(op: PackageOp): string[] {
  if (!isFolderOp(op)) return []
  if (op.kind === "duplicateFolder") return [op.sourceId]
  if (op.kind === "createFolder" || op.kind === "renameFolder" || op.kind === "reorder") return []
  return [op.folderId]
}

/** The folders whose packages a folder op edits: a duplicate's source is only read. */
export const editedFolderIds = (op: PackageOp): string[] =>
  op.kind === "duplicateFolder" ? [] : opFolderIds(op)

/** Whether a package page op can change what is replenished, so it needs a run. */
export const PACKAGE_PAGE_OP_NEEDS_RUN: Record<PackagePageOp["kind"], boolean> = {
  renamePackage: false,
  setPackageWindow: true,
  duplicatePackage: true,
  movePackage: true,
  setPackageStoresSource: true,
  setPackageActive: true,
}

export const FOLDER_OP_NEEDS_RUN: Record<FolderOp["kind"], boolean> = {
  createFolder: false,
  renameFolder: false,
  moveFolder: true,
  setFolderWindow: true,
  setFolderStores: true,
  setFolderStoresSource: true,
  setFolderActive: true,
  deleteFolder: true,
  removeSubfolder: false,
  reorder: false,
  duplicateFolder: true,
}
